/**
 * Global Catalog Page (SUPER_ADMIN)
 *
 * CRUD del catalogo global del producto: study_types y priorities con
 * institution_id NULL. Estos sirven como base para todas las IPS y
 * tambien como fuente de aprovisionamiento individual.
 */

import { useState } from 'react'
import {
  Layers,
  Loader2,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Flag,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/presentation/components/ui/Tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import {
  useGlobalStudyTypes,
  useCreateGlobalStudyType,
  useUpdateGlobalStudyType,
  useDeleteGlobalStudyType,
  useGlobalPriorities,
  useCreateGlobalPriority,
  useUpdateGlobalPriority,
  useDeleteGlobalPriority,
} from '@/application/global-catalog/useGlobalCatalog'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type {
  InstitutionStudyType,
  InstitutionPriority,
} from '@/application/institution-insumos/institution-insumos.service'

export default function CatalogPage() {
  const [tab, setTab] = useState<'studyTypes' | 'priorities'>('studyTypes')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Layers className="h-6 w-6" />
          Catalogo global
        </h1>
        <p className="text-[var(--foreground-muted)]">
          Insumos base del producto (tipos de estudio y prioridades). Visibles
          para todas las IPS y disponibles para aprovisionamiento individual
          desde el detalle de cada institucion.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'studyTypes' | 'priorities')}>
        <TabsList>
          <TabsTrigger value="studyTypes">
            <Layers className="h-4 w-4 mr-2" />
            Tipos de estudio
          </TabsTrigger>
          <TabsTrigger value="priorities">
            <Flag className="h-4 w-4 mr-2" />
            Prioridades
          </TabsTrigger>
        </TabsList>

        <TabsContent value="studyTypes">
          <GlobalStudyTypesSection />
        </TabsContent>

        <TabsContent value="priorities">
          <GlobalPrioritiesSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// =============================================================================
// Tipos de estudio globales
// =============================================================================

function GlobalStudyTypesSection() {
  const { data, isLoading } = useGlobalStudyTypes()
  const createMutation = useCreateGlobalStudyType()
  const updateMutation = useUpdateGlobalStudyType()
  const deleteMutation = useDeleteGlobalStudyType()

  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ name: string; description: string }>({
    name: '',
    description: '',
  })
  const [form, setForm] = useState({ code: '', name: '', description: '' })

  const studyTypes = data?.studyTypes ?? []

  const startEdit = (s: InstitutionStudyType) => {
    setEditingId(s.id)
    setDraft({ name: s.name, description: s.description ?? '' })
  }
  const cancelEdit = () => {
    setEditingId(null)
    setDraft({ name: '', description: '' })
  }
  const saveEdit = () => {
    if (!editingId) return
    updateMutation.mutate(
      {
        id: editingId,
        data: {
          name: draft.name,
          description: draft.description.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Tipo de estudio actualizado')
          cancelEdit()
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  const handleCreate = () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Codigo y nombre son requeridos')
      return
    }
    createMutation.mutate(
      {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Tipo de estudio agregado al catalogo global')
          setCreateOpen(false)
          setForm({ code: '', name: '', description: '' })
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Tipos de estudio del catalogo ({studyTypes.length})</CardTitle>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Agregar al catalogo
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : studyTypes.length === 0 ? (
          <p className="p-6 text-center text-[var(--foreground-muted)]">
            El catalogo global no tiene tipos de estudio. Agrega el primero.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codigo</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripcion</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-[120px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {studyTypes.map((s) => {
                const isEditing = editingId === s.id
                return (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.code}</TableCell>
                    <TableCell className="font-medium">
                      {isEditing ? (
                        <Input
                          value={draft.name}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                      ) : (
                        s.name
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[var(--foreground-muted)]">
                      {isEditing ? (
                        <Input
                          value={draft.description}
                          onChange={(e) =>
                            setDraft({ ...draft, description: e.target.value })
                          }
                        />
                      ) : (
                        s.description ?? '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.isActive ? 'default' : 'secondary'}>
                        {s.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={saveEdit}
                            disabled={updateMutation.isPending}
                          >
                            <Save className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(s)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (confirm(`¿Eliminar "${s.name}" del catalogo global?`)) {
                                deleteMutation.mutate(s.id, {
                                  onSuccess: () => toast.success('Eliminado'),
                                  onError: (err) => toast.error(getErrorMessage(err)),
                                })
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-[var(--destructive)]" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog crear */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar tipo de estudio al catalogo</DialogTitle>
            <DialogDescription>
              Visible para todas las IPS y disponible para aprovisionamiento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Codigo</label>
              <Input
                placeholder="Ej. RX-TORAX"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                placeholder="Ej. Radiografia de Torax"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descripcion (opcional)</label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// =============================================================================
// Prioridades globales
// =============================================================================

function GlobalPrioritiesSection() {
  const { data, isLoading } = useGlobalPriorities()
  const createMutation = useCreateGlobalPriority()
  const updateMutation = useUpdateGlobalPriority()
  const deleteMutation = useDeleteGlobalPriority()

  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState({ name: '', color: '#EF4444', sortOrder: 0 })
  const [form, setForm] = useState({
    code: '',
    name: '',
    color: '#EF4444',
    sortOrder: 1,
  })

  const priorities = data?.priorities ?? []

  const startEdit = (p: InstitutionPriority) => {
    setEditingId(p.id)
    setDraft({ name: p.name, color: p.color, sortOrder: p.sortOrder })
  }
  const cancelEdit = () => {
    setEditingId(null)
    setDraft({ name: '', color: '#EF4444', sortOrder: 0 })
  }
  const saveEdit = () => {
    if (!editingId) return
    updateMutation.mutate(
      {
        id: editingId,
        data: { name: draft.name, color: draft.color, sortOrder: draft.sortOrder },
      },
      {
        onSuccess: () => {
          toast.success('Prioridad actualizada')
          cancelEdit()
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  const handleCreate = () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Codigo y nombre son requeridos')
      return
    }
    createMutation.mutate(
      {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        color: form.color,
        sortOrder: form.sortOrder,
      },
      {
        onSuccess: () => {
          toast.success('Prioridad agregada al catalogo global')
          setCreateOpen(false)
          setForm({ code: '', name: '', color: '#EF4444', sortOrder: 1 })
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Prioridades del catalogo ({priorities.length})</CardTitle>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Agregar al catalogo
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : priorities.length === 0 ? (
          <p className="p-6 text-center text-[var(--foreground-muted)]">
            Sin prioridades en el catalogo. Agrega la primera.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codigo</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Orden</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-[120px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {priorities.map((p) => {
                const isEditing = editingId === p.id
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.code}</TableCell>
                    <TableCell className="font-medium">
                      {isEditing ? (
                        <Input
                          value={draft.name}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                      ) : (
                        p.name
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="color"
                          value={draft.color}
                          onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                          className="h-9 w-16"
                        />
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: p.color, color: p.textColor }}
                        >
                          {p.code}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="number"
                          min={0}
                          value={draft.sortOrder}
                          onChange={(e) =>
                            setDraft({ ...draft, sortOrder: Number(e.target.value) })
                          }
                          className="w-20"
                        />
                      ) : (
                        p.sortOrder
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.isActive ? 'default' : 'secondary'}>
                        {p.isActive ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={saveEdit}
                            disabled={updateMutation.isPending}
                          >
                            <Save className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(p)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (confirm(`¿Eliminar "${p.name}" del catalogo global?`)) {
                                deleteMutation.mutate(p.id, {
                                  onSuccess: () => toast.success('Eliminada'),
                                  onError: (err) => toast.error(getErrorMessage(err)),
                                })
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-[var(--destructive)]" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog crear */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar prioridad al catalogo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Codigo</label>
              <Input
                placeholder="Ej. URGENTE"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                placeholder="Ej. Urgente (4 horas)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Color</label>
                <Input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="h-10"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Orden</label>
                <Input
                  type="number"
                  min={0}
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
